import * as HL from './hl';
import * as Op from './ops';
import * as Types from './model/types';
import {Client} from './client';

// Must preserve behavior exactly!
//
// Simplifications implemented:
//   - remove limit ops prior to index ops
//   - remove offset ops prior to index ops
//   - convert groupBy.**.index() to filter where ** ops
//     must be dimension preserving
//   - convert index node that produces artifact to
//     project.artifact() (if descendent of project)
//   - convert index node that produces artifactVersion to
//     project.artifactVersion() (if descendent of project)

function groupByFnToFilterPred(
  groupByFn: Types.OutputNode,
  groupKey: any
): Types.OutputNode | undefined {
  if (groupByFn.type === 'string') {
    return Op.opStringEqual({
      lhs: groupByFn,
      rhs: Op.constString(groupKey),
    });
  } else if (groupByFn.type === 'number') {
    return Op.opNumberEqual({
      lhs: groupByFn,
      rhs: Op.constNumber(groupKey),
    });
  }
  if (!Types.isTypedDict(groupByFn.type)) {
    return;
  }
  const preds: Types.OutputNode[] = [];
  for (const key of Object.keys(groupByFn.type.propertyTypes)) {
    const propertyType = groupByFn.type.propertyTypes[key];
    if (propertyType === 'string') {
      preds.push(
        Op.opStringEqual({
          lhs: groupByFn.fromOp.inputs[key],
          rhs: Op.constString(groupKey[key]),
        })
      );
    } else if (propertyType === 'number') {
      preds.push(
        Op.opNumberEqual({
          lhs: groupByFn.fromOp.inputs[key],
          rhs: Op.constNumber(groupKey[key]),
        })
      );
    } else {
      return;
    }
  }
  return preds.reduce((accumulator, currentValue) =>
    Op.opAnd({lhs: accumulator as any, rhs: currentValue as any})
  );
}

async function simplifyNode(
  client: Client,
  node: Types.Node
): Promise<Types.Node | undefined> {
  if (node.nodeType !== 'output') {
    return;
  }
  const {fromOp} = node;
  if (fromOp.name === 'index') {
    const indexNode = fromOp.inputs.index;
    if (indexNode.nodeType !== 'const') {
      return;
    }
    const index = indexNode.val;
    const checkNode = fromOp.inputs.arr;
    if (checkNode.nodeType !== 'output') {
      return;
    }
    const checkFromOp = checkNode.fromOp;
    if (checkFromOp.name === 'limit') {
      const limitArr = checkFromOp.inputs.arr;
      const limitNode = checkFromOp.inputs.limit;
      if (limitNode.nodeType !== 'const') {
        return;
      }
      if (limitNode.val <= index) {
        // limit would chop the array shorter than we're trying
        // to index, which produces an error. Simplify should not
        // change behavior at all, so we don't simplify here.
        return;
      }
      return {
        ...node,
        fromOp: {
          name: 'index',
          inputs: {
            arr: limitArr,
            index: Op.constNumber(index),
          },
        },
      };
    } else if (checkFromOp.name === 'offset') {
      const offsetArr = checkFromOp.inputs.arr;
      const offsetNode = checkFromOp.inputs.offset;
      if (offsetNode.nodeType !== 'const') {
        return;
      }
      // skip over the limit node
      return {
        ...node,
        fromOp: {
          name: 'index',
          inputs: {
            arr: offsetArr,
            index: Op.constNumber(index + offsetNode.val),
          },
        },
      };
    }
    // an index with ancestor groupBy connected by dimension preserving
    // operations can be simplified to a filter.
    const foundGroupBy = HL.findChainedAncestor(
      checkNode,
      n => n.nodeType === 'output' && n.fromOp.name === 'groupby',
      n =>
        n.nodeType === 'output' &&
        (n.fromOp.name === 'filter' ||
          n.fromOp.name === 'sort' ||
          n.fromOp.name === 'map')
    );
    if (foundGroupBy != null) {
      if (foundGroupBy.nodeType !== 'output') {
        throw new Error('invalid');
      }
      const groupByFromOp = foundGroupBy.fromOp;
      const groupByArr = groupByFromOp.inputs.arr;
      const groupByFn = groupByFromOp.inputs.groupByFn;
      if (
        !Types.isFunction(groupByFn.type) ||
        groupByFn.type.inputTypes == null
      ) {
        throw new Error('invalid');
      }
      if (groupByFn.nodeType !== 'const') {
        return;
      }
      const tagNode = Op.opGroupGroupKey({
        obj: Op.opIndex({arr: checkNode, index: Op.constNumber(index)}),
      });
      const tagValue = await client.query(tagNode);
      const filterFn = groupByFnToFilterPred(groupByFn.val, tagValue);
      if (filterFn == null) {
        return;
      }
      return {
        ...node,
        fromOp: {
          name: 'filter',
          inputs: {
            arr: groupByArr,
            filterFn: Op.defineFunction(
              groupByFn.type.inputTypes,
              // ignore the named variables, we kept the same
              // input variable in groupByToFilterPred
              inputs => filterFn
            ),
          },
        },
      };
    }
    if (Types.isAssignableTo2(node.type, 'artifact')) {
      // TODO: not tested
      const projectNode = HL.findChainedAncestor(
        checkNode,
        n => n.nodeType === 'output' && n.type === 'project',
        n => true
      );
      if (projectNode == null) {
        return;
      }
      if (projectNode.nodeType !== 'output') {
        throw new Error('invalid');
      }
      const artifactTypeNode = Op.opArtifactTypeName({
        artifactType: Op.opArtifactType({artifact: node}),
      });
      const artifactNameNode = Op.opArtifactName({artifact: node});
      const [artifactType, artifactName] = await Promise.all([
        client.query(artifactTypeNode),
        client.query(artifactNameNode),
      ]);
      return {
        ...node,
        fromOp: {
          name: 'project-artifact',
          inputs: {
            project: projectNode,
            artifactType: Op.constString(artifactType),
            artifactName: Op.constString(artifactName),
          },
        },
      };
    }
    if (Types.isAssignableTo2(node.type, 'artifactVersion')) {
      // TODO: not tested
      const projectNode = HL.findChainedAncestor(
        checkNode,
        n => n.nodeType === 'output' && n.type === 'project',
        n => true
      );
      if (projectNode == null) {
        return;
      }
      if (projectNode.nodeType !== 'output') {
        throw new Error('invalid');
      }
      const artifactVersionNameNode = Op.opArtifactVersionName({
        artifactVersion: node as any,
      });
      const [artifactVersionName] = await client.query(artifactVersionNameNode);
      return {
        ...node,
        fromOp: {
          name: 'project-artifactVersion',
          inputs: {
            project: projectNode,
            artifactVersionName: Op.constString(artifactVersionName),
          },
        },
      };
    }
  }
  return;
}

async function simplifyPass(client: Client, node: Types.Node) {
  // simplify until we can't anymore
  let simpler: Types.Node | undefined = node;
  while (simpler != null) {
    simpler = await simplifyNode(client, node);
    if (simpler != null) {
      node = simpler;
    }
  }
  // Simplify inputs
  if (node.nodeType !== 'output') {
    return node;
  }
  const newInputNodes = await Promise.all(
    Object.values(node.fromOp.inputs).map(inNode => simplify(client, inNode))
  );
  const newInputs: Types.OpInputs = {};
  const argNames = Object.keys(node.fromOp.inputs);
  for (let i = 0; i < argNames.length; i++) {
    newInputs[argNames[i]] = newInputNodes[i];
  }
  return {
    ...node,
    fromOp: {
      ...node.fromOp,
      inputs: newInputs as any,
    },
  };
}

export async function simplify(
  client: Client,
  node: Types.Node
): Promise<Types.Node> {
  // If there are any get-tag calls, don't try to simplify
  if (
    HL.filterNodes(
      node,
      checkNode =>
        checkNode.nodeType === 'output' &&
        checkNode.fromOp.name === 'group-groupkey'
    ).length > 0
  ) {
    return node;
  }
  return simplifyPass(client, node);
}
